/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useField } from 'formik';

import DataList from '~/components/DataList';
import InputSearch from '~/components/Formik/InputSearch';
import Modal from '~/components/Modal';
import { parseArrayIntoString } from '~/pages/utils/parseArrayIntoString';
import { getDataListMetadataRequest } from '~/store/modules/dataList/actions';

import { Container } from './styles';

export interface DataListValue {
  id: string;
  label: string;
  value: string;
  parentId?: string;
}

interface DataListInputProps {
  fieldId: string;
  fieldName: string;
  fieldLabel: string;
  dataListId: string;
  isDisabled?: boolean;
  isMulti?: boolean;
  parentData?: {
    value: DataListValue[] | string | undefined;
    strictChildren?: boolean;
  };
  onChange?: (values?: DataListValue[]) => void;
  isRequired?: boolean;
}

const METADATA_SEPARATOR = ', ';

function DataListInput({
  fieldId,
  fieldName,
  fieldLabel,
  dataListId,
  isDisabled,
  isMulti,
  onChange,
  parentData,
  isRequired,
}: DataListInputProps) {
  const [dataListModal, setDataListModal] = useState<boolean>(false);

  const [field, meta, helpers] = useField<DataListValue[] | undefined>(
    fieldName,
  );
  const { value: dataListValue } = field;
  const { error, initialValue } = meta;
  const { setValue: setDataListValue } = helpers;

  const dispatch = useDispatch();

  const isChild = (
    source: DataListValue,
    parent?: Array<DataListValue> | string,
  ): boolean => {
    if (!parent) {
      return false;
    }

    if (parent instanceof Array) {
      return parent.some((data) => source.parentId === data.id);
    }

    return source.parentId === parent;
  };

  useEffect(() => {
    if (parentData && parentData.strictChildren) {
      const filteredValues = dataListValue?.filter((data) =>
        isChild(data, parentData.value),
      );

      setDataListValue(
        filteredValues && filteredValues.length > 0
          ? filteredValues
          : undefined,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentData?.value]);

  useEffect(() => {
    setDataListValue(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  return (
    <Container>
      <InputSearch<DataListValue>
        key={fieldId}
        name={fieldName}
        label={fieldLabel}
        value={dataListValue}
        onUpdateValues={(newValues) => {
          setDataListValue(newValues);
          if (onChange) onChange(newValues);
        }}
        disabled={isDisabled}
        isRequired={isRequired}
        error={error}
        onClickSearch={() => setDataListModal(true)}
      />
      {dataListModal && (
        <Modal
          title={fieldLabel}
          open={dataListModal}
          modalState={(opened) => setDataListModal(opened)}
          disableScrollBar
        >
          <DataList<DataListValue>
            isMulti={isMulti}
            values={dataListValue ?? null}
            callback={(values) => {
              setDataListValue(values);
              if (onChange) onChange(values);
              setDataListModal(false);
            }}
            onLoad={(...params) =>
              dispatch(getDataListMetadataRequest(...params))
            }
            genericParams={{
              formFieldId: dataListId,
              parentFormFieldValue:
                parentData?.value instanceof Array
                  ? parseArrayIntoString<DataListValue>(
                      parentData?.value,
                      'value',
                      METADATA_SEPARATOR,
                    )
                  : parentData?.value,
            }}
            pathToList="dataList"
            pathToLoading="loading"
            pathToTotal="totalMetadata"
            pathToData="metadata"
            mapProps={{
              id: 'id',
              label: 'meta_value',
              value: 'meta_value',
              parentId: 'parent_id',
            }}
          />
        </Modal>
      )}
    </Container>
  );
}

export default DataListInput;
